import {
	CharacterImageVariant,
	ImageContainerShape,
	renderCharacterImage,
} from '@datapad/common-controls';
import { useAppContext } from '@datapad/data-model';
import { Close, Person, Security, Shield, Visibility } from '@mui/icons-material';
import { Avatar, Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type React from 'react';
import { menuHorizontalPadding, menuVerticalPadding } from './Constants';

/**
 * {@link MenuHeader} input props.
 */
export interface MenuHeaderProps {
	/**
	 * Hides (collapses) the menu view.
	 */
	hideMenu(): void;
}

/**
 * Gets the display name for the permission level.
 */
function getPermissionLevelDisplay(permissionLevel: 'guest' | 'player' | 'game-master'): string {
	switch (permissionLevel) {
		case 'guest':
			return 'Guest';
		case 'player':
			return 'Player';
		case 'game-master':
			return 'Game Master';
	}
}

/**
 * Gets the icon for the permission level.
 */
function getPermissionLevelIcon(
	permissionLevel: 'guest' | 'player' | 'game-master',
	isViewingAs: boolean,
): React.ReactElement {
	if (isViewingAs) {
		return <Visibility fontSize="small" />;
	}
	switch (permissionLevel) {
		case 'guest':
			return <Person fontSize="small" />;
		case 'player':
			return <Shield fontSize="small" />;
		case 'game-master':
			return <Security fontSize="small" />;
	}
}

/**
 * Datapad menu header showing user identity and close button.
 *
 * @remarks
 * The header provides visual identity through:
 * - Avatar with initials or icon for quick recognition
 * - User/character name prominently displayed
 * - Role chip for permission level indication
 * - Subtle close button that doesn't compete with content
 */
export function MenuHeader(props: MenuHeaderProps): React.ReactElement {
	const { hideMenu } = props;

	const appContext = useAppContext();

	// Get the effective permission level (respects "View As" mode for GMs)
	const effectivePermissionLevel = appContext.getEffectivePermissionLevel();

	// Get the character if available (non-guests with a selected character)
	// When viewing as guest, don't show character even if one is selected
	const character =
		effectivePermissionLevel !== 'guest' &&
		'character' in appContext &&
		appContext.character !== undefined
			? appContext.getCharacter(appContext.character)
			: undefined;

	// Get the user's display name based on effective permission level
	// For players, show the character name; for GMs (viewing as GM), show "Game Master"; for guests, show "Guest"
	const displayName =
		character !== undefined
			? character.name
			: getPermissionLevelDisplay(effectivePermissionLevel);

	// Get the username for non-guests
	const user =
		appContext.permissionLevel !== 'guest' &&
		'user' in appContext &&
		appContext.user !== undefined
			? appContext.getUser(appContext.user)
			: undefined;
	const userName = user?.userName;

	// Get initials for the avatar fallback (first letter of first two words)
	const initials = displayName
		.split(' ')
		.slice(0, 2)
		.map((word) => word[0])
		.join('')
		.toUpperCase();

	return (
		<Box
			sx={(theme) => ({
				/*
				 * Header has a subtle background to create visual separation
				 * from the rest of the menu content.
				 */
				bgcolor: alpha(theme.palette.primary.main, 0.08),
				px: menuHorizontalPadding,
				py: menuVerticalPadding,
				width: '100%',
				boxSizing: 'border-box',
				overflow: 'hidden',
			})}
		>
			{/*
			 * Close button positioned in the top-right corner.
			 * Using absolute positioning within a relative container
			 * allows it to float without affecting the layout.
			 */}
			<Box sx={{ position: 'relative' }}>
				<Tooltip title="Close menu">
					<IconButton
						onClick={hideMenu}
						size="small"
						sx={{
							position: 'absolute',
							top: 0,
							right: 0,
							opacity: 0.7,
							'&:hover': {
								opacity: 1,
							},
						}}
					>
						<Close fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			{/* User identity section with avatar and name */}
			<Stack direction="row" spacing={2} alignItems="center">
				{/*
				 * Avatar area - shows character image if available, otherwise initials.
				 * The avatar provides quick visual recognition of the user.
				 */}
				{character !== undefined ? (
					renderCharacterImage(character, {
						maxWidthInPixels: 48,
						maxHeightInPixels: 48,
						containerShape: ImageContainerShape.Circle,
						variant: CharacterImageVariant.Profile,
					})
				) : (
					<Avatar
						sx={(theme) => ({
							bgcolor: theme.palette.primary.main,
							width: 48,
							height: 48,
							fontSize: '1.25rem',
						})}
					>
						{/* Show Person icon for guests, initials for others */}
						{effectivePermissionLevel === 'guest' ? <Person /> : initials}
					</Avatar>
				)}

				<Stack direction="column" spacing={0.5}>
					<Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
						{displayName}
					</Typography>

					{/* Username and role badge on the same line */}
					<Stack direction="row" spacing={1} alignItems="center">
						{/* Username on the left */}
						{userName !== undefined && (
							<Typography variant="body2" color="text.secondary">
								{userName}
							</Typography>
						)}

						{/*
						 * MUI Chip for role display, floated to the right.
						 * Chips provide a compact, visually distinct way to show metadata.
						 * Using 'outlined' variant for a subtle appearance.
						 */}
						<Chip
							icon={getPermissionLevelIcon(
								effectivePermissionLevel,
								effectivePermissionLevel !== appContext.permissionLevel,
							)}
							label={
								effectivePermissionLevel !== appContext.permissionLevel
									? `Viewing as ${getPermissionLevelDisplay(effectivePermissionLevel)}`
									: getPermissionLevelDisplay(effectivePermissionLevel)
							}
							size="small"
							variant="outlined"
							sx={{ ml: 'auto' }}
						/>
					</Stack>
				</Stack>
			</Stack>
		</Box>
	);
}
